import React from 'react';
import { Container, Typography, Box, Accordion, AccordionSummary, AccordionDetails, Chip, Stack } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink } from 'react-router-dom';
import { Link as MuiLink } from '@mui/material';

interface FAQ {
  question: string;
  answer: string | React.ReactNode;
  category: string;
}

const SalesTaxFAQ: React.FC = () => {
  const faqs: FAQ[] = [
    {
      category: 'Economic Nexus',
      question: 'What is economic nexus and when does it apply to my business?',
      answer: (
        <>
          Economic nexus is a tax obligation created when your business exceeds certain sales thresholds in a state, even without physical presence. After the 2018 <em>South Dakota v. Wayfair</em> Supreme Court decision, most states adopted economic nexus laws.
          <br/><br/>
          <strong>Common thresholds:</strong>
          <ul>
            <li>$100,000 in annual sales (most states)</li>
            <li>200 transactions per year (some states)</li>
            <li>Either threshold can trigger nexus obligation</li>
          </ul>
          Once you exceed a state's threshold, you must register for a sales tax permit, collect tax from customers, and file returns. Use our <MuiLink component={RouterLink} to="/tax/nexus-threshold">Nexus Threshold Checker</MuiLink> to monitor your status across all states.
        </>
      )
    },
    {
      category: 'Economic Nexus',
      question: 'Which states have economic nexus laws?',
      answer: 'All states with sales tax have economic nexus laws as of 2025. The five states without sales tax (Alaska, Delaware, Montana, New Hampshire, Oregon) do not have economic nexus requirements. However, some Alaska localities impose local sales taxes. View our complete state-by-state guide at the State Nexus Thresholds page.'
    },
    {
      category: 'Economic Nexus',
      question: 'How do I calculate my sales for nexus purposes?',
      answer: (
        <>
          Calculate sales on a <strong>rolling 12-month basis</strong> or calendar year, depending on the state. Include:
          <ul>
            <li>Gross sales to customers in that state</li>
            <li>Sales delivered to addresses in that state</li>
            <li>Both taxable and exempt sales (in most states)</li>
          </ul>
          <strong>Exclude:</strong>
          <ul>
            <li>Sales for resale (wholesale transactions)</li>
            <li>Sales tax collected</li>
            <li>Shipping charges (varies by state)</li>
          </ul>
          GoTruTax automatically tracks your sales by state when you connect Shopify, QuickBooks, Stripe, or upload CSV files.
        </>
      )
    },
    {
      category: 'Marketplace Facilitator',
      question: 'What is a marketplace facilitator and do I still need to collect tax?',
      answer: (
        <>
          A marketplace facilitator is a platform (like Amazon, eBay, Etsy, Walmart Marketplace) that facilitates retail sales. Under marketplace facilitator laws, these platforms must collect and remit sales tax on behalf of their sellers.
          <br/><br/>
          <strong>Your responsibilities:</strong>
          <ul>
            <li><strong>Sales through marketplaces:</strong> The platform handles tax collection and remittance</li>
            <li><strong>Direct sales (your own website):</strong> You must collect and remit tax</li>
            <li><strong>Mixed channels:</strong> Track marketplace vs. direct sales separately</li>
          </ul>
          <strong>Important:</strong> Even if marketplaces handle tax collection, you may still need to register in some states and report these sales on your returns (usually as $0 taxable sales with marketplace sales noted separately).
        </>
      )
    },
    {
      category: 'Marketplace Facilitator',
      question: 'Does Shopify collect sales tax for me?',
      answer: 'Shopify is NOT a marketplace facilitator for most merchants. You are responsible for collecting and remitting sales tax on Shopify sales unless you are selling through Shopify Markets (which may handle tax in certain jurisdictions). GoTruTax integrates with Shopify to automatically track your sales, calculate correct tax rates, and monitor nexus thresholds across all states.'
    },
    {
      category: 'Registration & Filing',
      question: 'How do I register for a sales tax permit?',
      answer: (
        <>
          Registration process varies by state but typically involves:
          <ol>
            <li><strong>Visit the state's tax authority website</strong> (e.g., California's CDTFA, Texas Comptroller)</li>
            <li><strong>Complete online registration form</strong> with business information (EIN, address, business structure)</li>
            <li><strong>Provide effective date</strong> when you first established nexus</li>
            <li><strong>Receive sales tax permit</strong> (usually immediate or within 2-3 weeks)</li>
            <li><strong>Display permit</strong> as required by state law</li>
          </ol>
          Most states offer free registration, but some charge fees ($10-$50). GoTruTax's <MuiLink component={RouterLink} to="/tax/registration">Tax Registration Manager</MuiLink> helps you track registration statuses, deadlines, and renewal requirements across all states.
        </>
      )
    },
    {
      category: 'Registration & Filing',
      question: 'How often do I need to file sales tax returns?',
      answer: (
        <>
          Filing frequency depends on your sales volume in each state:
          <ul>
            <li><strong>Monthly:</strong> High-volume sellers (typically $10,000+ per month in the state)</li>
            <li><strong>Quarterly:</strong> Medium-volume sellers (most common for small businesses)</li>
            <li><strong>Annually:</strong> Low-volume sellers (varies by state, some require minimum sales)</li>
          </ul>
          States assign your filing frequency when you register, based on estimated sales volume. You can request frequency changes if your sales volume changes significantly. Miss a filing deadline and you face penalties of 5-25% plus interest.
        </>
      )
    },
    {
      category: 'Registration & Filing',
      question: 'What happens if I do not register when required?',
      answer: (
        <>
          Failing to register for sales tax when required can result in:
          <ul>
            <li><strong>Back taxes owed:</strong> You're liable for uncollected tax (comes out of your pocket)</li>
            <li><strong>Penalties:</strong> 5-25% of tax owed per state</li>
            <li><strong>Interest charges:</strong> Typically 8-12% annually, compounding</li>
            <li><strong>Audit risk:</strong> States actively pursue non-compliant remote sellers</li>
            <li><strong>Business disruption:</strong> License suspension, inability to do business in state</li>
          </ul>
          <strong>Voluntary Disclosure Agreements (VDAs):</strong> If you discover you should have registered in the past, many states offer VDA programs with reduced penalties. Act quickly to minimize liability.
        </>
      )
    },
    {
      category: 'Tax Calculation',
      question: 'How do I calculate the correct sales tax rate?',
      answer: (
        <>
          Sales tax rates in the US are complex because they consist of multiple layers:
          <ul>
            <li><strong>State rate:</strong> Base rate set by state (0-7.25%)</li>
            <li><strong>County rate:</strong> Additional county tax (if applicable)</li>
            <li><strong>City rate:</strong> Additional city tax (if applicable)</li>
            <li><strong>Special district rate:</strong> Transit, tourism, or other special districts</li>
          </ul>
          <strong>Example - Los Angeles, CA:</strong><br/>
          State: 7.25% + County: 0.25% + City: 0% + District: 1% = <strong>8.5% total</strong>
          <br/><br/>
          Over 10,000 tax jurisdictions exist in the US. Use our <MuiLink component={RouterLink} to="/tax/calculator">Free Sales Tax Calculator</MuiLink> for accurate rates based on zip code or full address.
        </>
      )
    },
    {
      category: 'Tax Calculation',
      question: 'Is shipping taxable?',
      answer: (
        <>
          Shipping taxability varies significantly by state:
          <ul>
            <li><strong>Taxable in most states:</strong> If shipping is included in product price or separately stated but mandatory</li>
            <li><strong>Not taxable:</strong> If separately stated and optional in many states</li>
            <li><strong>State-specific rules:</strong> Some states never tax shipping, others always do</li>
          </ul>
          <strong>Examples:</strong>
          <ul>
            <li><strong>California:</strong> Shipping is not taxable if separately stated</li>
            <li><strong>Texas:</strong> Shipping is taxable if included in product price</li>
            <li><strong>New York:</strong> Shipping is generally not taxable if separately stated</li>
          </ul>
          GoTruTax applies correct shipping taxability rules for each state automatically.
        </>
      )
    },
    {
      category: 'Tax Calculation',
      question: 'Are digital products taxable?',
      answer: (
        <>
          Digital product taxability varies widely by state and product type:
          <br/><br/>
          <strong>Generally Taxable States (for most digital products):</strong><br/>
          Arizona, Colorado, Connecticut, District of Columbia, Hawaii, Idaho, Kentucky, Louisiana, Maine, Maryland, Massachusetts, Michigan, Minnesota, Mississippi, Nebraska, New Jersey, New Mexico, New York, North Carolina, Ohio, Pennsylvania, Rhode Island, South Carolina, South Dakota, Tennessee, Texas, Utah, Vermont, Washington, West Virginia, Wisconsin, Wyoming
          <br/><br/>
          <strong>Categories:</strong>
          <ul>
            <li><strong>Software as a Service (SaaS):</strong> Taxable in 20+ states</li>
            <li><strong>Downloadable software:</strong> Taxable in 25+ states</li>
            <li><strong>Digital content (ebooks, music, videos):</strong> Varies by state</li>
            <li><strong>Streaming services:</strong> Increasingly taxable (15+ states)</li>
          </ul>
          Digital product taxability changes frequently. Consult our <MuiLink component={RouterLink} to="/advisor">AI Tax Advisor</MuiLink> for current rules.
        </>
      )
    },
    {
      category: 'Product Taxability',
      question: 'Is food taxable?',
      answer: (
        <>
          Food taxability depends on the type of food and the state:
          <ul>
            <li><strong>Grocery items (unprepared food):</strong> Not taxable in most states, but taxable in Alabama, Arkansas, Hawaii, Idaho, Illinois (1%), Kansas, Mississippi, Missouri, Oklahoma, South Dakota, Tennessee, Utah, Virginia</li>
            <li><strong>Prepared food (ready-to-eat):</strong> Taxable in most states</li>
            <li><strong>Candy:</strong> Taxable even when groceries aren't in many states</li>
            <li><strong>Soft drinks:</strong> Taxable even when groceries aren't in many states</li>
            <li><strong>Restaurant meals:</strong> Taxable in all states with sales tax</li>
          </ul>
          <strong>State-specific examples:</strong>
          <ul>
            <li><strong>California:</strong> Groceries not taxable, prepared food taxable</li>
            <li><strong>Texas:</strong> Groceries not taxable, prepared food and candy taxable</li>
            <li><strong>Illinois:</strong> Groceries taxed at 1%, prepared food at full rate</li>
          </ul>
        </>
      )
    },
    {
      category: 'Product Taxability',
      question: 'Are services taxable?',
      answer: (
        <>
          Service taxability is highly state-specific:
          <ul>
            <li><strong>Most states:</strong> Services are generally NOT taxable (opposite of goods)</li>
            <li><strong>Hawaii, New Mexico, South Dakota:</strong> Tax most services</li>
            <li><strong>Washington:</strong> Taxes many services including personal services, business services</li>
          </ul>
          <strong>Commonly taxed services (in certain states):</strong>
          <ul>
            <li>Installation and repair services</li>
            <li>Information services</li>
            <li>Personal grooming services</li>
            <li>Maintenance contracts</li>
            <li>Telecommunications services (taxable in most states)</li>
          </ul>
          <strong>Professional services typically NOT taxable:</strong> Legal, accounting, consulting, medical services
        </>
      )
    },
    {
      category: 'Compliance & Automation',
      question: 'How can I automate sales tax compliance?',
      answer: (
        <>
          Tax automation platforms like GoTruTax streamline the entire compliance process:
          <br/><br/>
          <strong>Key automation features:</strong>
          <ol>
            <li><strong>Nexus monitoring:</strong> Automatic tracking of sales by state with alerts when approaching thresholds</li>
            <li><strong>Tax calculation:</strong> Real-time calculation of accurate rates for 10,000+ jurisdictions</li>
            <li><strong>Platform integrations:</strong> Connect Shopify, QuickBooks, Stripe, or import CSV files</li>
            <li><strong>Transaction management:</strong> Automatic categorization and tracking of all transactions</li>
            <li><strong>Return preparation:</strong> Generate state-ready returns with variance analysis</li>
            <li><strong>Compliance monitoring:</strong> Dashboard showing registration status, filing deadlines, and compliance health</li>
            <li><strong>AI tax advisor:</strong> Instant answers to tax questions using official state sources</li>
          </ol>
          Start with GoTruTax's free Monitor plan - no credit card required. <MuiLink component={RouterLink} to="/get-started">Get started in 2 minutes</MuiLink>.
        </>
      )
    },
    {
      category: 'Compliance & Automation',
      question: 'What integrations does GoTruTax support?',
      answer: (
        <>
          GoTruTax integrates with major platforms to automatically import your transaction data:
          <ul>
            <li><strong>Shopify:</strong> Real-time sync of orders, refunds, and customer data</li>
            <li><strong>QuickBooks Online:</strong> Sync invoices, sales receipts, and customer records</li>
            <li><strong>Stripe:</strong> Import payment transactions with customer location data</li>
            <li><strong>CSV Import:</strong> Upload transactions from any platform or custom system</li>
          </ul>
          <strong>Coming soon:</strong> WooCommerce, BigCommerce, Square, Amazon Seller Central
          <br/><br/>
          All integrations support automatic data refresh, so your compliance dashboard stays current without manual work. Set up integrations in minutes from your dashboard.
        </>
      )
    },
    {
      category: 'Compliance & Automation',
      question: 'How much does sales tax compliance cost?',
      answer: (
        <>
          Sales tax compliance costs vary based on your approach:
          <br/><br/>
          <strong>Manual compliance (DIY):</strong>
          <ul>
            <li>Registration fees: $0-$50 per state</li>
            <li>Time cost: 5-20 hours/month for multi-state businesses</li>
            <li>Error risk: High (costly penalties and interest)</li>
            <li><strong>Total annual cost:</strong> $5,000-$25,000 (staff time + errors)</li>
          </ul>
          <strong>Full-service providers:</strong>
          <ul>
            <li>Setup fees: $500-$2,000</li>
            <li>Monthly fees: $100-$500+ per state</li>
            <li><strong>Total annual cost:</strong> $15,000-$100,000+</li>
          </ul>
          <strong>GoTruTax (automated platform):</strong>
          <ul>
            <li>Monitor plan: <strong>Free</strong> (nexus monitoring, AI advisor, basic alerts)</li>
            <li>Standard plan: <strong>$49/month</strong> (automated calculations, multi-state compliance)</li>
            <li>Pro plan: <strong>$149/month</strong> (everything + audit protection, API access)</li>
            <li><strong>Total annual cost:</strong> $0-$1,788</li>
          </ul>
          Start free, upgrade as you grow. <MuiLink component={RouterLink} to="/get-started">Try GoTruTax now</MuiLink>.
        </>
      )
    },
    {
      category: 'Specific Scenarios',
      question: 'Do I need to charge sales tax on sales to other businesses (B2B)?',
      answer: (
        <>
          Generally, <strong>YES</strong> - you charge sales tax on B2B sales unless the buyer provides a valid resale certificate or exemption certificate.
          <br/><br/>
          <strong>When to collect tax:</strong>
          <ul>
            <li>Business buyer does not provide exemption certificate</li>
            <li>Product/service is for business's own use (not for resale)</li>
            <li>Business buyer is in a taxable state/jurisdiction</li>
          </ul>
          <strong>When NOT to collect tax:</strong>
          <ul>
            <li>Business buyer provides valid resale certificate (buying for resale)</li>
            <li>Business buyer provides exemption certificate (government, nonprofit, etc.)</li>
            <li>Product qualifies for a specific exemption</li>
          </ul>
          <strong>Important:</strong> Keep all exemption certificates on file for at least 3-7 years (varies by state). You're responsible if an exemption is later found invalid.
        </>
      )
    },
    {
      category: 'Specific Scenarios',
      question: 'How do I handle returns and refunds for sales tax?',
      answer: (
        <>
          When you issue a refund, you must also refund the sales tax collected:
          <br/><br/>
          <strong>Process:</strong>
          <ol>
            <li><strong>Issue refund:</strong> Refund purchase price + sales tax to customer</li>
            <li><strong>Record transaction:</strong> Track refund with original transaction details</li>
            <li><strong>Adjust tax owed:</strong> Deduct refunded tax from your next sales tax return</li>
            <li><strong>Report on return:</strong> Most states have separate line items for refunds/credits</li>
          </ol>
          <strong>Timing considerations:</strong>
          <ul>
            <li>Refund in same period: Reduce that period's tax liability</li>
            <li>Refund in different period: Claim credit on return for period when refund issued</li>
            <li>Some states allow carryforward of excess credits</li>
          </ul>
          GoTruTax automatically tracks refunds and adjusts your tax liability when you connect Shopify, QuickBooks, or other platforms.
        </>
      )
    },
    {
      category: 'Specific Scenarios',
      question: 'What if I sell internationally? Do I charge US sales tax?',
      answer: (
        <>
          <strong>Short answer:</strong> No US sales tax on international sales (sales to customers outside the US).
          <br/><br/>
          <strong>Key points:</strong>
          <ul>
            <li><strong>Export sales:</strong> Generally not subject to US sales tax</li>
            <li><strong>Destination matters:</strong> Determine destination based on shipping address</li>
            <li><strong>Keep records:</strong> Document international sales for audit purposes</li>
            <li><strong>VAT/GST:</strong> You may need to collect foreign taxes depending on country (e.g., EU VAT, Canada GST)</li>
          </ul>
          <strong>Nexus still applies:</strong> If you have inventory or operations in a foreign country, you may create nexus there. Consult local tax advisors for foreign tax obligations.
          <br/><br/>
          <strong>Marketplace facilitators:</strong> Platforms like Amazon may handle international tax collection and remittance automatically.
        </>
      )
    }
  ];

  // Generate structured data for FAQPage
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": typeof faq.answer === 'string' ? faq.answer : faq.question // Simplified for schema
      }
    }))
  };

  const categories = Array.from(new Set(faqs.map(faq => faq.category)));

  return (
    <>
      <Helmet>
        <title>Sales Tax FAQ: Economic Nexus, Compliance, Marketplace Facilitator | GoTruTax</title>
        <meta name="description" content="Comprehensive sales tax FAQ covering economic nexus, marketplace facilitator laws, registration, filing, product taxability, and compliance automation. Expert answers to your tax questions." />
        <meta name="keywords" content="sales tax FAQ, economic nexus questions, marketplace facilitator FAQ, sales tax compliance questions, nexus threshold FAQ, tax registration help, sales tax answers" />
        <link rel="canonical" href="https://gotrutax.com/faq" />
        
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <Container maxWidth="md" sx={{ py: 8 }}>
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography variant="h1" sx={{ fontSize: '2.5rem', fontWeight: 800, mb: 2 }}>
            Sales Tax Compliance FAQ
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
            Common questions about economic nexus, marketplace facilitator laws, registration, filing, and tax automation
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" gap={1}>
            {categories.map(category => (
              <Chip key={category} label={category} size="small" />
            ))}
          </Stack>
        </Box>

        {categories.map(category => (
          <Box key={category} sx={{ mb: 4 }}>
            <Typography variant="h2" sx={{ fontSize: '1.5rem', fontWeight: 700, mb: 2, mt: 4 }}>
              {category}
            </Typography>
            {faqs.filter(faq => faq.category === category).map((faq, index) => (
              <Accordion key={index} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" component="div" sx={{ lineHeight: 1.7 }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        ))}

        {/* CTA Section */}
        <Box sx={{ mt: 8, p: 4, bgcolor: 'primary.main', color: 'white', borderRadius: 2, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
            Still Have Questions?
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
            Try our free AI Tax Advisor for instant answers based on official state tax codes
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <MuiLink
              component={RouterLink}
              to="/advisor"
              sx={{
                display: 'inline-block',
                bgcolor: 'white',
                color: 'primary.main',
                px: 4,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover': {
                  bgcolor: 'grey.100'
                }
              }}
            >
              Try AI Tax Advisor
            </MuiLink>
            <MuiLink
              component={RouterLink}
              to="/contact"
              sx={{
                display: 'inline-block',
                bgcolor: 'transparent',
                color: 'white',
                px: 4,
                py: 1.5,
                border: '2px solid white',
                borderRadius: 2,
                fontWeight: 600,
                textDecoration: 'none',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              Contact Support
            </MuiLink>
          </Stack>
        </Box>

        {/* Related Resources */}
        <Box sx={{ mt: 6, p: 3, bgcolor: 'background.default', borderRadius: 2 }}>
          <Typography variant="h3" sx={{ fontSize: '1.25rem', fontWeight: 600, mb: 2 }}>
            Related Resources
          </Typography>
          <Stack spacing={1}>
            <MuiLink component={RouterLink} to="/blog/sales-tax-compliance-guide-2025">→ Complete Sales Tax Compliance Guide 2025</MuiLink>
            <MuiLink component={RouterLink} to="/resources/state-nexus-thresholds">→ Economic Nexus Thresholds by State</MuiLink>
            <MuiLink component={RouterLink} to="/tax/calculator">→ Free Sales Tax Calculator</MuiLink>
            <MuiLink component={RouterLink} to="/resources">→ All Resources & Tools</MuiLink>
          </Stack>
        </Box>
      </Container>
    </>
  );
};

export default SalesTaxFAQ;
